import { uniqueId, isEmpty, isArray } from 'lodash';
import { buildFornecedor } from '@/utils/fornecedor-utils';
import { FinalidadeCompra, SituacaoRequisicao } from 'px-business-components';
import { computeFrom, initialStateCamposErp } from '../campos-erp/validate-campos-erp';
import { initialStateCamposErp as initialStateCamposRequisicaoErp, computeFrom as computeFromRequisicao } from '../campos-erp/requisicao/validate-campos-erp';

const TEMP_KEYS = ['$$id', '$$hasFileLoading'];

export const initialStateProduto = (tipoErp, data) => ({
  $$id: uniqueId(),
  produto: null,
  quantidade: 0,
  unidadeMedida: null,
  especificacaoProduto: null,
  arquivos: [],
  observacaoInterna: null,
  observacaoExterna: null,
  projeto: data?.projeto ?? null,
  centroCusto: data?.centroCusto ?? null,
  finalidadeCompra: data?.finalidadeCompra ?? FinalidadeCompra.keys.USO_CONSUMO.key,
  dataPrevisaoEntrega: data?.dataEntrega ?? null,
  cadastrosAdicionais: initialStateCamposErp(tipoErp),
  situacao: SituacaoRequisicao.keys.RECEBIDA.key,
  $$hasFileLoading: false,
});

export const initialState = tipoErp => ({
  comprador: null,
  identificador: null,
  locaisEntrega: null,
  compradoresResponsaveis: [],
  urgencia: null,
  dataEntrega: null,
  centroCusto: null,
  projeto: null,
  observacaoInterna: null,
  observacaoExterna: null,
  finalidadeCompra: FinalidadeCompra.keys.USO_CONSUMO.key,
  situacao: SituacaoRequisicao.keys.RECEBIDA.key,
  cadastrosAdicionais: initialStateCamposRequisicaoErp(tipoErp),
  produtos: [
    initialStateProduto(tipoErp),
  ],
  fornecedores: [],
  arquivos: [],
});

export const computeInitialStateProduto = (produto, tipoErp) => {
  if (!produto) {
    return initialStateProduto(tipoErp);
  }

  const computedProduto = { ...initialStateProduto(tipoErp) };

  computedProduto.id = produto.id ? produto.id : undefined;

  Object.keys(computedProduto)
    .filter(key => !TEMP_KEYS.includes(key))
    .forEach((key) => {
      computedProduto[key] = produto[key];
    });

  if (isEmpty(computedProduto.arquivos)) {
    computedProduto.arquivos = produto.produto?.arquivos ?? [];
  }

  return computedProduto;
};

export const computeInitialStateCopiaProduto = (produto, tipoErp) => {
  const computedProduto = { ...initialStateProduto(tipoErp) };
  Object.keys(computedProduto)
    .filter(key => !TEMP_KEYS.includes(key))
    .forEach((key) => {
      computedProduto[key] = produto[key];
    });

  computedProduto.dataPrevisaoEntrega = null;
  computedProduto.cadastrosAdicionais = computeFrom(tipoErp, produto.cadastrosAdicionais);

  if (isEmpty(computedProduto.arquivos)) {
    computedProduto.arquivos = produto.produto?.arquivos ?? [];
  }

  return computedProduto;
};

export const copyProduto = (produto, tipoErp, data) => {
  const computedProduto = initialStateProduto(tipoErp);

  Object.keys(computedProduto)
    .filter(key => !TEMP_KEYS.includes(key))
    .forEach((key) => {
      computedProduto[key] = produto[key];
    });

  computedProduto.id = produto.id ? produto.id : undefined;
  computedProduto.dataPrevisaoEntrega = produto.dataPrevisaoEntrega ?? data?.dataEntrega ?? null;
  computedProduto.projeto = produto.projeto ?? data?.projeto ?? null;
  computedProduto.centroCusto = produto.centroCusto ?? data?.centroCusto ?? null;

  computedProduto.cadastrosAdicionais = isArray(produto.cadastrosAdicionais)
    ? computeFrom(tipoErp, produto.cadastrosAdicionais) : { ...produto.cadastrosAdicionais };

  if (isEmpty(computedProduto.arquivos)) {
    computedProduto.arquivos = produto.produto?.arquivos ?? [];
  }
  return computedProduto;
};

export const computeInitialCopia = (data, tipoErp) => {
  const copia = { ...initialState(tipoErp) };

  Object.keys(copia)
    .forEach((key) => {
      copia[key] = data[key];
    });

  copia.situacao = SituacaoRequisicao.keys.RECEBIDA.key;
  copia.dataEntrega = null;
  copia.identificador = undefined;
  copia.cadastrosAdicionais = computeFromRequisicao(tipoErp, data.cadastrosAdicionais);

  copia.produtos = copia.produtos.map(p => ({
    ...computeInitialStateCopiaProduto(p, tipoErp),
  }));

  if (!isEmpty(copia.fornecedores)) {
    copia.fornecedores = copia.fornecedores.map(f => buildFornecedor(data, f));
  }

  return copia;
};

export const computeInitialRascunho = (data, tipoErp) => {
  const rascunho = { ...initialState(tipoErp) };

  Object.keys(rascunho)
    .forEach((key) => {
      rascunho[key] = data[key];
    });

  rascunho.id = data.id;
  rascunho.identificador = data.identificador?.split('-').slice(1).join('-') ?? undefined;
  rascunho.codigoInterno = data.codigoInterno ?? undefined;

  if (!isEmpty(rascunho.produtos)) {
    rascunho.produtos = rascunho.produtos.map(p => ({
      ...copyProduto(p, tipoErp, data),
      codigoInterno: p?.codigoInterno ?? undefined,
    }));
  } else {
    rascunho.produtos = [initialStateProduto(tipoErp, data)];
  }

  if (!isEmpty(rascunho.fornecedores)) {
    rascunho.fornecedores = rascunho.fornecedores.map(f => buildFornecedor(data, f));
  }

  return rascunho;
};

export const computeInitialEdicao = (data, tipoErp) => {
  const edicao = {};

  Object.keys(initialState(tipoErp))
    .forEach((key) => {
      edicao[key] = data[key];
    });

  edicao.id = data.id;
  edicao.codigoInterno = data.codigoInterno ?? undefined;
  edicao.identificador = data.identificador?.split('-').slice(1).join('-') ?? undefined;
  edicao.cadastrosAdicionais = computeFromRequisicao(tipoErp, data.cadastrosAdicionais);

  edicao.produtos = edicao.produtos.map(p => ({
    ...copyProduto(p, tipoErp),
    codigoInterno: p?.codigoInterno ?? undefined,
  }));


  if (!isEmpty(edicao.fornecedores)) {
    edicao.fornecedores = edicao.fornecedores.map(f => buildFornecedor(data, f));
  }

  return edicao;
};


export default {
  initialState,
  initialStateProduto,
};
